import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PropTypes } from 'prop-types';
import { reset, change } from 'redux-form';
import { push } from 'connected-react-router';
import NotFoundPage from 'components/Errors/NotFoundPage';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import moment from 'moment';

import {
  customerDataLoad,
  customerAccountLink,
  customerAccountUnlink,
  showCustomerIdChangeModal,
  hideCustomerIdChangeModal,
  processCustomerIdChange,
  clearCompletedCustomerIdChange,
  clearCustomerData,
  getParentImyForms,
  showDeleteFormModal,
  hideDeleteFormModal,
  deleteForms,
  takeQueueItem,
  hideOwnershipModal,
  deleteSupportingDoc,
  editCustomer,
  cancelEditCustomer,
  updateCustomer
} from 'actions';

import getCertifictionStatus from 'common/Forms/CertificationStatus';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import AddCustomerAccountForm from 'components/Customer/AddCustomerAccountForm';
import CustomerAccountList from 'components/Customer/CustomerAccountList';
import CustomerFormList from 'components/Customer/CustomerFormList';
import CustomerAddresses from 'components/Customer/CustomerAddresses';
import CustomerJotrs from 'components/Customer/CustomerJotrs';
import WithholdingRates from 'components/WithholdingRates';

import CustomerIdChangeModal from 'components/Modals/CustomerIdChangeModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import ImyFormTable from 'components/ImyFormTable';
import DeleteFormModal from 'components/Modals/DeleteFormModal';
import ChangeOwnershipModal from 'components/Modals/ChangeOwnershipModal';
import ActiveChip from 'components/Common/ActiveChip';
import DirectChip from 'components/Common/DirectChip';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import MoveForm from 'components/MoveForm';
import CloneForm from 'components/CloneForm';

import ReactTable from 'components/ReactTable';
import supportingDocsColumnDefs from 'components/Customer/CustomerSupportingDocsList/supportingDocsColumnDefs';
import SupportingDocsActions from 'components/Customer/CustomerSupportingDocsList/supportingDocsActions';
import SupportingDocs from 'components/Customer/CustomerSupportingDocsList/supportingDocsUpload';
import { toastr } from 'react-redux-toastr';

class Customer extends Component {
  static propTypes = {
    permissions: PropTypes.arrayOf(PropTypes.string),
    jurisdictions: PropTypes.arrayOf(PropTypes.shape()),
    settings: PropTypes.shape({
      showWithholding: PropTypes.bool,
      showJointAccounts: PropTypes.bool
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    isCustomerIdChangeModalShown: PropTypes.bool.isRequired,
    isLoadingData: PropTypes.bool.isRequired,
    storeCustomer: PropTypes.shape(),
    returnedExistingCustomerId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    history: PropTypes.shape({
      push: PropTypes.func
    }),
    customerDataLoadError: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    }),
    parentImyForms: PropTypes.shape({}),
    isGettingParentImyForms: PropTypes.bool,
    isDeleteFormModalShown: PropTypes.bool,
    isDeletingForm: PropTypes.bool,
    deleteFormError: PropTypes.string,
    isOwnershipModalShown: PropTypes.bool,
    changeOwnershipItem: PropTypes.shape(),
    customerSupportingDocs: PropTypes.arrayOf(PropTypes.shape()),
    isGettingCustomers: PropTypes.bool,
    initSupportingDocGridCriteria: PropTypes.shape(),
    isEditingCustomer: PropTypes.bool,
    isSubmittingCustomer: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    jurisdictions: null,
    storeCustomer: {
      name: '',
      accounts: [],
      forms: [],
      address: {}
    },
    returnedExistingCustomerId: '',
    history: null,
    customerDataLoadError: null,
    parentImyForms: null,
    isGettingParentImyForms: false,
    isDeleteFormModalShown: false,
    isDeletingForm: false,
    deleteFormError: null,
    isOwnershipModalShown: false,
    changeOwnershipItem: null,
    customerSupportingDocs: null,
    isGettingCustomers: false,
    initSupportingDocGridCriteria: null,
    isEditingCustomer: false,
    isSubmittingCustomer: false
  };

  static getForeignTin(tin, notRequired) {
    if (!tin) {
      return notRequired ? <i>FTIN Declared as Not Legally Required</i> : 'N/A';
    }
    const cleanedTin = tin.replace(/\s/g, '').toLowerCase();
    if (cleanedTin.includes('notlegallyrequired')) {
      return !notRequired ? <React.Fragment>{ tin } (<i>FTIN Not Correctly Declared as Legally Required</i>)</React.Fragment> : <i>FTIN Declared as Not Legally Required</i>;
    }

    return notRequired ? <i>FTIN Declared as Not Legally Required</i> : tin;
  }

  constructor(props) {
    super(props);

    const { initSupportingDocGridCriteria, permissions, storeCustomer } = props;

    const supportingDocsGridCriteria = initSupportingDocGridCriteria || {
      columnDefs: supportingDocsColumnDefs(permissions),
      pageIndex: 0,
      sortBy: [],
      filters: [],
      pageCount: 0,
      pageSize: 10,
      itemCount: 0,
      selectedItems: [],
      selectedItem: null
    };

    this.state = {
      isUnlinkAccountModalShown: false,
      isUnlinkingAccount: false,
      unlinkAccountError: null,
      accountId: null,
      formToDelete: null,
      data: [],
      supportingDocsGridCriteria,
      loading: true,
      loadingTime: 500,
      initialLoaded: false,
      emailAddress: storeCustomer?.emailAddress ?? null
    };
  }

  componentDidMount() {
    const { dispatch, match, permissions } = this.props;
    dispatch(customerDataLoad(match.params.id));
    if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree])) {
      dispatch(getParentImyForms(match.params.id, null));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { dispatch, returnedExistingCustomerId, history, customerSupportingDocs } = this.props;
    const { initialLoaded } = this.state;
    if (returnedExistingCustomerId && prevProps.returnedExistingCustomerId !== returnedExistingCustomerId) {
      dispatch(customerDataLoad(returnedExistingCustomerId));
      setTimeout(() => {
        dispatch(clearCompletedCustomerIdChange());
        history.push(`/customer/${ returnedExistingCustomerId }`);
      }, 2000);
    }

    if (customerSupportingDocs !== prevProps.customerSupportingDocs) {
      this.processData(customerSupportingDocs, customerSupportingDocs.length);
      this.hideLoadingAnimation();
      if (!initialLoaded) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ initialLoaded: true });
      }
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(clearCustomerData());
    dispatch(cancelEditCustomer());
  }

  onEmailAddressChange(e) {
    this.setState({ emailAddress: e.target.value });
  }

  onUpdateCancel(e) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(cancelEditCustomer());
    this.setState({ emailAddress: storeCustomer?.emailAddress });
  }

  onUpdateSubmit(e) {
    const { dispatch, storeCustomer } = this.props;
    const { emailAddress } = this.state;
    dispatch(updateCustomer(storeCustomer.id, emailAddress));
  }

  onEditEmailAddressClick() {
    const { dispatch, storeCustomer } = this.props;
    dispatch(editCustomer());
    this.setState({ emailAddress: storeCustomer.emailAddress });
  }

  getCustomerName() {
    const { storeCustomer } = this.props;
    if (storeCustomer === null || (!storeCustomer.customerId && !storeCustomer.name)) {
      return 'N/A';
    }

    let customerName = storeCustomer.customerId;

    if (!customerName) {
      customerName = storeCustomer.name;
    } else if (storeCustomer.name !== null && storeCustomer.name !== customerName) {
      customerName = `${ customerName } - ${ storeCustomer.name }`;
    }

    return customerName;
  }

  getJurisdictionName(country) {
    const { jurisdictions } = this.props;

    if (country && jurisdictions && jurisdictions.length > 0) {
      const matches = jurisdictions.filter(x => x.code === country || x.name === country);
      if (matches && matches.length > 0) {
        return `${ matches[0].name } (${ matches[0].code })`;
      }
    }
    return country != null && country !== '' ? country : 'N/A';
  }

  // eslint-disable-next-line class-methods-use-this
  viewSupportingDocument = (item) => {
    if (item) {
      window.open(`customers/supporting-docs/${ item.itemId }`, '_blank'); // to open new page
      return;
    }
    toastr.error('', 'Sorry, this document cannot be viewed');
  };

  deleteSupportingDocument = (item) => {
    if (item) {
      const { dispatch } = this.props;
      dispatch(deleteSupportingDoc(item.itemId));
    }
  };

  openDeleteFormModal = (item) => {
    const { dispatch } = this.props;
    this.setState({ formToDelete: item });
    dispatch(showDeleteFormModal([item]));
  };

  closeDeleteFormModal = () => {
    const { dispatch } = this.props;
    this.setState({ formToDelete: null });
    dispatch(hideDeleteFormModal());
  };

  processDeleteForms = () => {
    const { dispatch, match } = this.props;
    const { formToDelete } = this.state;
    dispatch(deleteForms([formToDelete.id], () => {
      this.closeDeleteFormModal();
      dispatch(customerDataLoad(match.params.id));
    }));
  };

  openUnlinkAccountModal = (accountId) => {
    this.setState({ isUnlinkAccountModalShown: true, accountId });
  };

  closeUnlinkAccountModal = () => {
    this.setState({ isUnlinkAccountModalShown: false });
  };

  confirmUnlinkAccount = () => {
    const { accountId } = this.state;
    this.processAccountUnlink(accountId);
    this.setState({ isUnlinkAccountModalShown: false });
  };

  closeFromOwnershipModal = (item) => {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal(item));
  };

  reAssignItem = () => {
    const { dispatch, changeOwnershipItem } = this.props;
    if (changeOwnershipItem != null && changeOwnershipItem.id != null) {
      dispatch(takeQueueItem('form', changeOwnershipItem.id, changeOwnershipItem.accountId));
      dispatch(hideOwnershipModal());
    }
  };

  continueFromOwnershipModal = () => {
    const { dispatch, changeOwnershipItem } = this.props;
    dispatch(hideOwnershipModal());
    let redirectUrl = `/forms/${ changeOwnershipItem.formType.toLowerCase() }/${ changeOwnershipItem.id }`;
    if (changeOwnershipItem.accountId) {
      redirectUrl = `${ redirectUrl }?account=${ changeOwnershipItem.accountId }`;
    }
    dispatch(push(redirectUrl));
  };

  getActions = () => {
    const { permissions } = this.props;

    return SupportingDocsActions({
      permissions,
      deleteSupportingDocument: this.deleteSupportingDocument,
      viewSupportingDocument: this.viewSupportingDocument,
      canDelete: this.canDeleteSupportingDocs()
    });
  };

  hideLoadingAnimation = () => {
    const { loadingTime } = this.state;
    const { isGettingCustomers } = this.props;
    const loadingInterval = setInterval(() => {
      if (!isGettingCustomers) {
        this.setState({ loading: false });
        clearInterval(loadingInterval);
      }
    }, loadingTime);
  };

  processData = (items, itemCount) => {
    const { supportingDocsGridCriteria } = this.state;
    const data = [];
    if (items != null) {
      items.forEach((item) => {
        const datum = {
          itemId: item?.id,
          originalFileName: item?.originalFileName,
          typeDescription: item?.typeDescription,
          uploadDate: (item?.uploadDate === null || item?.uploadDate === '') ? 'N/A' : moment(item.uploadDate).format('MMM DD, YYYY HH:mm:ss')
        };
        data.push(datum);
      });
      const pageCount = Math.ceil(itemCount / supportingDocsGridCriteria.pageSize);
      this.setState({ data, supportingDocsGridCriteria: { ...supportingDocsGridCriteria, itemCount, pageCount } });
    }
  };

  processAccountLink(addedAccountData) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerAccountLink(storeCustomer.id, addedAccountData));
    dispatch(reset('addCustomerAccountForm'));
    dispatch(change('addCustomerAccountForm', 'accountNumber', null));
  }

  processAccountUnlink(accountId) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerAccountUnlink(storeCustomer.id, accountId));
  }

  openCustomerIdChangeModal() {
    const { dispatch } = this.props;
    dispatch(showCustomerIdChangeModal());
  }

  closeCustomerIdChangeModal() {
    const { dispatch } = this.props;
    dispatch(hideCustomerIdChangeModal());
  }

  processCustomerIdChangeModal(updatedCustomer) {
    const { dispatch } = this.props;
    dispatch(processCustomerIdChange(updatedCustomer));
  }

  canDeleteSupportingDocs() {
    const { storeCustomer } = this.props;
    if (storeCustomer?.forms && storeCustomer.forms.length > 0) {
      return storeCustomer.forms.filter(form => form.workflowStageDescription === 'Complete').length === 0
             && storeCustomer.forms.filter(form => form.workflowStageDescription === 'Complete' && form.validUntil < new Date()).length === 0;
    }
    return true;
  }

  render() {
    const { permissions, isLoadingData, storeCustomer, isCustomerIdChangeModalShown, customerDataLoadError, match,
      settings, parentImyForms, isGettingParentImyForms, dispatch,
      isDeleteFormModalShown, isDeletingForm, deleteFormError,
      isOwnershipModalShown, changeOwnershipItem, isEditingCustomer, isSubmittingCustomer
    } = this.props;
    const {
      isUnlinkAccountModalShown,
      isUnlinkingAccount,
      unlinkAccountError,
      formToDelete,
      data,
      supportingDocsGridCriteria,
      loading,
      initialLoaded,
      emailAddress
    } = this.state;
    const regexp = /(?:T-)(.*)(?:-MP)(.*)/;
    let certData = null;
    if (storeCustomer && storeCustomer.accounts && storeCustomer.accounts.length === 1 && storeCustomer.accounts[0]) {
      certData = storeCustomer.accounts[0].formCertificationData ?? storeCustomer.accounts[0].importCertificationData;
    }
    const numAccounts = storeCustomer && storeCustomer.accounts ? storeCustomer.accounts.length : 0;
    const numJointAccounts = storeCustomer && storeCustomer.accounts ? storeCustomer.accounts.filter(x => x.account && x.account.isJointAccount).length : 0;

    return (
      <div className="container customer-page-panel">
        { customerDataLoadError && (customerDataLoadError.code !== 0
          && <NotFoundPage caption={ customerDataLoadError.message } />
        ) }
        { !customerDataLoadError && (isLoadingData || storeCustomer == null) && <LoadingWrapper loaded={ !isLoadingData && storeCustomer !== null } />}
        { !customerDataLoadError && !isLoadingData && storeCustomer !== null
        && <div className="customer-panel">
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                Customer: { this.getCustomerName() }
                <ActiveChip isActive={ storeCustomer?.isActive } />
                <DirectChip isDirect={ storeCustomer?.isDirect } />
              </div>
              { storeCustomer?.isDirect && <AccessControl allowedPermissions={ [ActivityPerms.customerEdit] }>
                <button type="submit" className="btn btn-primary" onClick={ () => this.openCustomerIdChangeModal() }>
                  Edit Customer ID
                </button>
              </AccessControl> }
            </div>
            <div className="panel-body">
              <div className="no-shrink">
                <div className="panel-body-caption">
                  <h2><strong>Customer Details:</strong></h2>
                </div>
                <div className="columns">
                  <div className="col-5 col-xl-5 col-md-6">
                    <div className="columns">
                      { (numAccounts === 1 && numJointAccounts === 0) && <React.Fragment>
                        <div className="col-5 panel-item-name">
                          <p><strong>Certification Status: </strong></p>
                        </div>
                        <div className="col-7 panel-item-value">
                          <p>{ storeCustomer && getCertifictionStatus(storeCustomer.certificationStatus) }</p>
                        </div>
                      </React.Fragment> }
                      <div className="col-5 panel-item-name">
                        <p><strong>Name: </strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <p>{storeCustomer.name ? storeCustomer.name : 'N/A'}</p>
                      </div>
                      <div className="col-5 panel-item-name">
                        <p><strong>Customer ID:</strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <p>{storeCustomer.customerId ? storeCustomer.customerId : 'N/A'}</p>
                      </div>
                      <div className="col-5 panel-item-name">
                        <p><strong>Citizenship / Incorporation Country: </strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <p>{ this.getJurisdictionName(storeCustomer?.citizenshipCode) }</p>
                      </div>
                      <div className="col-5 panel-item-name">
                        <p><strong>Permanent Address(es):</strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <CustomerAddresses
                          modalTitle="Permanent Addresses"
                          moreButtonText="Show More Permanent Addresses"
                          addresses={ storeCustomer?.permanentAddresses }
                        />
                      </div>
                      <div className="col-5 panel-item-name">
                        <p><strong>Mailing Address(es):</strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <CustomerAddresses
                          modalTitle="Mailing Addresses"
                          moreButtonText="Show More Mailing Addresses"
                          addresses={ storeCustomer?.mailingAddresses }
                          showActions={ AccessControl.hasPermission(permissions, [ActivityPerms.customerEdit]) }
                        />
                      </div>
                      <div className="col-5 panel-item-name">
                        <p><strong>Phone Number:</strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <p>{storeCustomer != null && storeCustomer.phoneNumber ? storeCustomer.phoneNumber : 'N/A'}</p>
                      </div>
                      <div className="col-5 panel-item-name">
                        <p><strong>Email Address:</strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        { !isEditingCustomer && <p className="email-address">
                          { storeCustomer && storeCustomer.emailAddress ? storeCustomer.emailAddress : 'N/A' }
                          </p> }
                        { !isEditingCustomer && AccessControl.hasPermission(permissions, [ActivityPerms.customerEdit]) && storeCustomer?.isDirect === true && <button
                          type="button"
                          className="btn btn-link"
                          onClick={ () => this.onEditEmailAddressClick() }
                        >
                          <i className="icon icon-edit" />
                        </button> }
                        { isEditingCustomer && AccessControl.hasPermission(permissions, [ActivityPerms.customerEdit]) && <React.Fragment>
                          <input
                            className="form-input"
                            maxLength="254"
                            disabled={ isSubmittingCustomer }
                            value={ emailAddress ?? '' }
                            onChange={ e => this.onEmailAddressChange(e) }
                          />
                          <button
                            type="button"
                            className="btn btn-link editing"
                            disabled={ isSubmittingCustomer }
                            onClick={ () => this.onUpdateSubmit() }
                          ><i className="icon icon-check" /></button>
                          <button
                            type="button"
                            className="btn btn-link editing"
                            disabled={ isSubmittingCustomer }
                            onClick={ () => this.onUpdateCancel() }
                          ><i className="icon icon-cross" /></button>
                        </React.Fragment> }
                      </div>
                    </div>
                  </div>
                  <div className="col-5 col-xl-5 col-md-6">
                    { storeCustomer != null && storeCustomer.accounts != null && storeCustomer.accounts.length === 1
                      && <div className="columns">
                        <div className="col-5 panel-item-name">
                          <p><strong>Treaty Country: </strong></p>
                        </div>
                        <div className="col-7 panel-item-value">
                          <p>{ this.getJurisdictionName(certData?.treatyCountryIsoCode) }</p>
                        </div>
                        { certData?.lobCategoryCode && certData.lobCategoryCode !== '' && <React.Fragment>
                          <div className="col-5 panel-item-name">
                            <p><strong>LOB: </strong></p>
                          </div>
                          <div className="col-7 panel-item-value">
                            <p>{certData?.lobCategoryCode ? certData.lobCategoryCode : 'N/A'}</p>
                          </div>
                        </React.Fragment>}
                        <div className="col-5 panel-item-name">
                          <p><strong>US TIN:</strong></p>
                        </div>
                        <div className="col-7 panel-item-value">
                          <p>{certData?.tin ? certData.tin : 'N/A'}</p>
                        </div>
                        <div className="col-5 panel-item-name">
                          <p><strong>FTIN:</strong></p>
                        </div>
                        <div className="col-7 panel-item-value">
                          <p>{ Customer.getForeignTin(certData?.foreignTin, certData?.foreignTinNotRequired) }</p>
                        </div>
                        <div className="col-5 panel-item-name">
                          <p><strong>GIIN:</strong></p>
                        </div>
                        <div className="col-7 panel-item-value">
                          <p>{certData?.giin ? certData.giin : 'N/A'}</p>
                        </div>
                        <div className="col-5 panel-item-name">
                          <p><strong>Part 2 GIIN:</strong></p>
                        </div>
                        <div className="col-7 panel-item-value">
                          <p>{certData?.part2Giin ? certData.part2Giin : 'N/A'}</p>
                        </div>
                      </div> }
                    { (storeCustomer != null && storeCustomer.accounts != null && storeCustomer.accounts.length > 1)
                      && <p className="toast toast-info text-center mb">
                        To view certification data for each account holder<br />click the &apos;Display additional details&apos; icon within the &apos;Accounts&apos; Grid
                      </p> }
                    { storeCustomer?.irsBNotice != null && <div className="columns">
                      <div className="col-5 panel-item-name">
                        <p><strong>IRS B Notice:</strong></p>
                      </div><div className="col-7 panel-item-value">
                        <p>{storeCustomer?.irsBNotice}</p>
                      </div><div className="col-5 panel-item-name">
                        <p><strong>IRS B Notice Date:</strong></p>
                      </div><div className="col-7 panel-item-value">
                        <p>{storeCustomer?.bNoticeReceivedDate != null ? moment(storeCustomer.bNoticeReceivedDate).format('MMM DD, YYYY') : 'N/A'}</p>
                      </div>
                    </div> }
                    { storeCustomer?.irsCNotice != null && <div className="columns">
                      <div className="col-5 panel-item-name">
                        <p><strong>IRS C Notice:</strong></p>
                      </div><div className="col-7 panel-item-value">
                        <p>{storeCustomer?.irsCNotice}</p>
                      </div><div className="col-5 panel-item-name">
                        <p><strong>IRS C Notice Date:</strong></p>
                      </div><div className="col-7 panel-item-value">
                        <p>{storeCustomer?.cNoticeReceivedDate != null ? moment(storeCustomer.cNoticeReceivedDate).format('MMM DD, YYYY') : 'N/A'}</p>
                      </div>
                    </div> }
                    <div className="columns">
                      <div className="col-5 panel-item-name">
                        <p><strong>Tax Residenc(ies):</strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <CustomerJotrs jotrs={ storeCustomer?.taxResidencies } showActions={ AccessControl.hasPermission(permissions, [ActivityPerms.customerEdit]) } />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <CustomerIdChangeModal
                id={ storeCustomer.id }
                customerId={ storeCustomer.customerId }
                isShown={ isCustomerIdChangeModalShown }
                onConfirmCustomerIdChange={ updatedCustomer => this.processCustomerIdChangeModal(updatedCustomer) }
                onCancel={ () => this.closeCustomerIdChangeModal() }
                isTempUser={ storeCustomer.customerId && regexp.exec(storeCustomer.customerId) !== null ? regexp.exec(storeCustomer.customerId).length > 0 : false }
              />
            </div>
          </div>
          <div className="panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2><strong>Forms: </strong></h2>
              </div>
              <div>
                <CustomerFormList
                  permissions={ permissions }
                  customer={ storeCustomer }
                  onDeleteForm={ item => this.openDeleteFormModal(item) }
                />
              </div>
              { storeCustomer?.forms && storeCustomer.forms.length > 0 && <div className="columns">
                <div className="col-6 col-sm-12">
                  { AccessControl.hasPermission(permissions, [ActivityPerms.scannedEdit]) && <MoveForm
                    title="Move Form"
                    introText="Moving a form removes it from the current account and associates it with the target account and customer.
                      The form will be automatically identified as pending initial review and require revalidation. You cannot move workflow complete forms."
                    forms={ storeCustomer.forms.filter(f => f.workflowStage !== 100) } // Only Non-Workflow Complete Forms
                    sourceCustomerId={ storeCustomer.id }
                    onSuccess={ () => dispatch(customerDataLoad(storeCustomer.id)) }
                  /> }
                </div>
                <div className="col-6 col-sm-12">
                  { AccessControl.hasPermission(permissions, [ActivityPerms.scannedEdit]) && <CloneForm
                    title="Clone Form"
                    introText="Cloning a form creates and associates a new copy of the form on the target account.&nbsp;
                      Where the legal entity set up differs between accounts, the form will be automatically identified as pending initial review and will require validation."
                    forms={ storeCustomer.forms }
                    onSuccess={ () => dispatch(customerDataLoad(storeCustomer.id)) }
                  /> }
                </div>
              </div> }
            </div>
          </div>
          <div className="panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2><strong>Supporting Documents: </strong></h2>
              </div>
              <ReactTable
                data={ data }
                gridCriteria={ supportingDocsGridCriteria }
                onGridCriteriaChanged={ () => null }
                actions={ this.getActions() }
                loading={ loading }
                initialLoaded={ initialLoaded }
                collapseHeight
                enablePagination={ false }
              />
              <SupportingDocs
                customerId={ storeCustomer.id }
                permissions={ permissions }
                documents={ data }
              />
            </div>
          </div>
          <div className="panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2><strong>Accounts: </strong></h2>
              </div>
              <div>
                <CustomerAccountList
                  customer={ storeCustomer }
                  permissions={ permissions }
                  jointAccountsEnabled={ settings.showJointAccounts }
                  onConfirmAccountUnlink={ accountId => this.openUnlinkAccountModal(accountId) }
                />
              </div>
              { settings.showJointAccounts && <AccessControl allowedPermissions={ [ActivityPerms.customerEdit] }>
                <div className="panel-body-caption">
                  <h2><strong>Link an Account: </strong></h2>
                </div>
                <div className="col-8">
                  <AddCustomerAccountForm onConfirmAccountLink={ updateData => this.processAccountLink(updateData) } />
                </div>
              </AccessControl> }
            </div>
          </div>
          { AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree]) && <div className="panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2><strong>IMY Trees: </strong></h2>
              </div>
              <div>
                <ImyFormTable ImyForms={ parentImyForms } isGettingImyForms={ isGettingParentImyForms } dispatch={ dispatch } />
              </div>
            </div>
          </div>}
          { settings && settings.showWithholding && AccessControl.hasPermission(permissions, [ActivityPerms.whdViewRates]) && <div className="panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2><strong>Withholding Rates: </strong></h2>
              </div>
              <div>
                <WithholdingRates customerId={ match.params.id } />
              </div>
            </div>
          </div> }
          <ConfirmModal
            key="unlinkAccount"
            isShown={ isUnlinkAccountModalShown }
            isPerformingAction={ isUnlinkingAccount }
            onConfirm={ () => this.confirmUnlinkAccount() }
            onCancel={ () => this.closeUnlinkAccountModal() }
            error={ unlinkAccountError }
            title="Unlink"
            message="Are you sure you want to unlink this account?"
          />
          <DeleteFormModal
            isShown={ isDeleteFormModalShown }
            isDeletingForm={ isDeletingForm }
            onConfirmDelete={ () => this.processDeleteForms() }
            onCancel={ () => this.closeDeleteFormModal() }
            deleteFormError={ deleteFormError }
            items={ formToDelete !== null ? [formToDelete] : [] }
          />
          { changeOwnershipItem != null && <ChangeOwnershipModal
            isShown={ isOwnershipModalShown }
            onAssign={ () => this.reAssignItem() }
            onContinueWithoutAssign={ () => this.continueFromOwnershipModal() }
            onCancel={ () => this.closeFromOwnershipModal() }
            workflowRecord={ changeOwnershipItem }
          /> }
        </div> }
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings
    },
    common: {
      jurisdictions
    },
    customer: {
      storeCustomer,
      isLoadingData,
      isCustomerIdChangeModalShown,
      returnedExistingCustomerId,
      customerDataLoadError,
      customerSupportingDocs,
      isEditingCustomer,
      isSubmittingCustomer
    },
    forms: {
      isGettingParentImyForms,
      parentImyForms,
      isDeleteFormModalShown,
      isDeletingForm,
      deleteFormError,
      supportingDocs,
      supportingDocsGridCriteria
    },
    workflow: {
      isOwnershipModalShown,
      changeOwnershipItem,
      isGettingCustomers
    }
  } = state;
  return {
    permissions,
    jurisdictions,
    storeCustomer,
    customerSupportingDocs,
    isLoadingData,
    isCustomerIdChangeModalShown,
    returnedExistingCustomerId,
    customerDataLoadError,
    settings,
    isGettingParentImyForms,
    parentImyForms,
    isDeleteFormModalShown,
    isDeletingForm,
    deleteFormError,
    isOwnershipModalShown,
    changeOwnershipItem,
    supportingDocs,
    isGettingCustomers,
    initSupportingDocGridCriteria: supportingDocsGridCriteria,
    isEditingCustomer,
    isSubmittingCustomer
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(Customer, 'Customer')));
